import { motion } from "framer-motion";
import { Check, Pen } from "lucide-react";

export const PDFToolsMockup = () => {
  return (
    <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-gradient-to-br from-violet-600 via-violet-500 to-purple-400 p-3 md:p-4 flex items-center justify-center shadow-xl overflow-hidden relative">
      {/* Ambient effects */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
      <div className="absolute -top-10 -left-10 w-24 h-24 bg-purple-300/30 rounded-full blur-2xl" />
      <div className="absolute -bottom-8 -right-8 w-20 h-20 bg-violet-300/20 rounded-full blur-xl" />
      
      {/* Main document with 3D effect */}
      <div className="relative" style={{ perspective: "500px" }}>
        <motion.div
          initial={{ rotateY: 20, rotateX: 10, scale: 0.9 }}
          whileInView={{ rotateY: 0, rotateX: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          {/* Paper document */}
          <div 
            className="w-16 h-20 md:w-20 md:h-24 bg-white rounded-lg shadow-2xl relative overflow-hidden"
            style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
          >
            {/* PDF header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 h-3 md:h-4 flex items-center px-1.5">
              <span className="text-[6px] md:text-[7px] text-white font-bold tracking-wide">PDF</span>
            </div>
            
            {/* Document content */}
            <div className="p-2 space-y-1.5">
              <div className="w-10 md:w-12 h-1 bg-gray-200 rounded-full" />
              <div className="w-8 md:w-10 h-1 bg-gray-200 rounded-full" />
              <div className="w-11 md:w-14 h-1 bg-gray-100 rounded-full" />
              <div className="w-6 md:w-8 h-1 bg-gray-200 rounded-full" />
              <div className="w-9 md:w-11 h-1 bg-gray-100 rounded-full" />
              
              {/* Signature area */}
              <div className="mt-2 pt-1.5 border-t border-dashed border-gray-200">
                <motion.div
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.6, ease: "easeOut" }}
                  className="relative h-3 md:h-4"
                >
                  <svg className="w-full h-full" viewBox="0 0 50 15">
                    <motion.path
                      d="M5 10 Q 10 5, 15 10 T 25 10 T 35 8 Q 40 6, 45 9"
                      fill="none"
                      stroke="#4F46E5"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
                    />
                  </svg>
                </motion.div>
              </div>
            </div>

            {/* Scanning line effect */}
            <motion.div
              initial={{ top: "10%" }}
              whileInView={{ top: "100%" }}
              transition={{ delay: 0.2, duration: 1, ease: "easeInOut" }}
              className="absolute left-0 right-0 h-6 bg-gradient-to-b from-violet-400/30 via-violet-400/10 to-transparent pointer-events-none"
              style={{ boxShadow: "0 -2px 20px 2px rgba(139, 92, 246, 0.3)" }}
            />
          </div>

          {/* Signature pen */}
          <motion.div
            initial={{ x: 30, y: -20, rotate: -30, opacity: 0 }}
            whileInView={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5, type: "spring" }}
            className="absolute -right-2 bottom-3 md:bottom-4 w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg"
          >
            <Pen className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
          </motion.div>
        </motion.div>

        {/* Success badge */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.4, type: "spring", stiffness: 400 }}
          className="absolute -bottom-2 -right-2 w-6 h-6 md:w-7 md:h-7 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
        >
          <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" strokeWidth={3} />
        </motion.div>
      </div>
    </div>
  );
};
